import { useState, useEffect } from 'react';
import { apiService } from '../utils/api';
import type { PushSubscription } from '../types/api';

// Utility to convert base64 VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkSupport();
  }, []);

  const checkSupport = async () => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true);
      await checkSubscription();
    }
    setLoading(false);
  };

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const existingSubscription = await registration.pushManager.getSubscription();
      
      if (existingSubscription) {
        const p256dhKey = existingSubscription.getKey('p256dh');
        const authKey = existingSubscription.getKey('auth');
        
        if (p256dhKey && authKey) {
          setIsSubscribed(true);
          setSubscription({
            id: '', // Browser doesn't provide ID, we'll get it from backend if needed
            endpoint: existingSubscription.endpoint,
            p256dh: btoa(String.fromCharCode(...new Uint8Array(p256dhKey))),
            auth: btoa(String.fromCharCode(...new Uint8Array(authKey))),
            created_at: new Date().toISOString()
          });
        }
      }
    } catch (error) {
      console.error('Error checking push subscription:', error);
      setError('Failed to check push subscription status');
    }
  };

  const subscribe = async () => {
    try {
      setError(null);
      
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setError('Notification permission denied');
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      let newSubscription;
      try {
        const vapidKey = "BIJpBHtMyhSpsZ2KvmmFXianNcLoTTk7CytrMR2gIEa6hbEy4hMNo6QB_EHlFW-TzYS9fOwuRKXGFGF6ksOgZAw";
        if (!vapidKey) {
          throw new Error('VITE_VAPID_PUBLIC_KEY is not set');
        }
        const applicationServerKey = urlBase64ToUint8Array(vapidKey);
        newSubscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey
        });
      } catch (err) {
        // console.error('[Push] pushManager.subscribe() error:', err);
        throw err;
      }

      const p256dhKey = newSubscription.getKey('p256dh');
      const authKey = newSubscription.getKey('auth');
      
      if (!p256dhKey || !authKey) {
        setError('Failed to get push subscription keys');
        return false;
      }

      // Send subscription to backend
      const response = await apiService.subscribeToPushNotifications({
        endpoint: newSubscription.endpoint,
        p256dh: btoa(String.fromCharCode(...new Uint8Array(p256dhKey))),
        auth: btoa(String.fromCharCode(...new Uint8Array(authKey)))
      });

      if (response.success) {
        setIsSubscribed(true);
        setSubscription({
          id: '', // Backend doesn't return subscription ID in response
          endpoint: newSubscription.endpoint,
          p256dh: btoa(String.fromCharCode(...new Uint8Array(p256dhKey))),
          auth: btoa(String.fromCharCode(...new Uint8Array(authKey))),
          created_at: new Date().toISOString()
        });
        return true;
      } else {
        setError('Failed to subscribe to push notifications');
        return false;
      }
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          setError('Push notifications are blocked. Please enable them in your browser settings.');
        } else if (error.name === 'NotSupportedError') {
          setError('Push notifications are not supported in this browser.');
        } else {
          setError('Failed to subscribe to push notifications. Please try again.');
        }
      } else {
        setError('An unexpected error occurred while subscribing to push notifications.');
      }
      return false;
    }
  };

  const unsubscribe = async () => {
    try {
      setError(null);
      
      if (subscription) {
        // Unsubscribe from browser push service
        const registration = await navigator.serviceWorker.ready;
        const existingSubscription = await registration.pushManager.getSubscription();
        if (existingSubscription) {
          await existingSubscription.unsubscribe();
        }
        
        // Remove subscription from backend
        const response = await apiService.unsubscribeFromPushNotifications();
        
        if (response.success) {
          setIsSubscribed(false);
          setSubscription(null);
          return true;
        } else {
          setError('Failed to unsubscribe from push notifications');
          return false;
        }
      }
      return true;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      setError('Failed to unsubscribe from push notifications. Please try again.');
      return false;
    }
  };

  const clearError = () => {
    setError(null);
  };

  return {
    isSupported,
    isSubscribed,
    subscription,
    loading,
    error,
    subscribe,
    unsubscribe,
    clearError
  };
}; 